import { ComponentType } from "@angular/cdk/portal";
import { MatDialog } from "@angular/material/dialog";
import { catchError, map, Observable, of, ReplaySubject, tap } from "rxjs";
import { AdminsApi, AdminSummary, Metadata, PagedAdminSummaryResponse } from "src/app/hickory-api";
import { AuthService } from "../../authentication/auth.service";
import { LogHelper } from "../../logging/log-helper";
import { toDictionary } from "../../model/utility";
import { SelectUsersData } from "../../select-users/select-users.component";
import { ContactSelection } from "../../model";
import { ActionToggleMap } from "./helpers/action-toggle-map";
import { AuthzConstants } from "./helpers/authz.constants";
import { DataSource } from "@angular/cdk/collections";

interface IHasName {
  id?: number;
  name?: string;
}

interface IResponse<TModel> {
  items?: Array<TModel>;
}

export interface AuthZDialogData {
  tenantId?: number;
  currentSelection: AuthorizationViewModel[]
}

export interface AuthZDialogResult {
  selected: AuthorizationViewModel[]
}

class AuthzDataSource extends DataSource<AuthorizationViewModel> {
  
  private _items = new ReplaySubject<AuthorizationViewModel[]>();

  connect(): Observable<readonly AuthorizationViewModel[]> {
    return this._items;
  }

  disconnect(): void {}

  next(items: AuthorizationViewModel[]) {
    this._items.next(items);
  }
}

export abstract class GenericAuthzCollection {

  public abstract can: boolean;

  protected _items: AuthorizationViewModel[];
  protected actionToggleMap = new ActionToggleMap();

  public readActionName = 'read';
  public canDeleteItems = true;
  public readonly columns = ['_authz_col_name', ...this.actions, '_authz_col_removeItem'];

  public get length() { return this._items?.length || 0; }
  public get hasValues() { return this.length > 0; }
  public get ids() { return this.map(x => x.id) }

  protected log: LogHelper;

  datasource: AuthzDataSource

  constructor(
    public readonly tenantId: number,
    protected actions: string[],
    public type: string,
    public icon: string,
  ) {
    this.log = new LogHelper(`AuthorizationCollection.${this.type}`);
  }

  map<T>(fn: (item: AuthorizationViewModel) => T): T[] {
    return this._items?.map(fn);
  }

  every(fn: (item: AuthorizationViewModel) => boolean): boolean {
    return this._items?.every(fn) || false;
  }

  find(fn: (item: AuthorizationViewModel) => boolean): AuthorizationViewModel {
    return this._items?.find(fn);
  }

  isActionReadonly(item: AuthorizationViewModel, action: string, metadata?: Metadata): boolean {
    if (item.isActionReadOnly(action)) {
      return true;
    }
    // only scan metadata if we need to
    return metadata ? AuthzConstants.isEveryoneActionEnabled(action, metadata) : false;
  }

  setItems(items: AuthorizationViewModel[]) {
    this.datasource = new AuthzDataSource();
    this._items = items;
    this.datasource.next(items);
  }

  removeItem(item: AuthorizationViewModel) {
    const itemIndex = this.getItemIndex(item);
    if (itemIndex !== -1) {
      this._items.splice(itemIndex, 1);
    }
    this.datasource.next(this._items);
    return this;
  }

  toggleItemAction(item: AuthorizationViewModel, action: string) {
    const itemIndex = this.getItemIndex(item);
    if (itemIndex !== -1) {
      this._items[itemIndex].toggleAction(action); 
      this.actionToggleMap.setValueOnUserEvent(item, action);
    }
    this.datasource.next(this._items);
    return this;
  }

  toggleActionForAllItems(action: string, checked: boolean) {
    for (let i = 0; i < this._items.length; i++) {
      const item = this._items[i];
      const lastWasUserEnabled = this.actionToggleMap.lastActionWasUserEvent(item, action);
      if (this.isActionReadonly(this._items[i], action) && lastWasUserEnabled) continue;
      if (checked) {
        this._items[i].grantAction(action);
      } else {
        if (!lastWasUserEnabled) {
          this._items[i].revokeAction(action);
        }
      }
      this.actionToggleMap.setValueOnAutoEvent(this._items[i], action);
    }
    this.datasource.next(this._items);
    return this;
  }

  getUpdatedMetadata(metadata: Metadata): Metadata {
    const updatedMetadata = { ...metadata };
    for (const action of this.actions) {
      updatedMetadata.tags[this.getTagKey(action)] = this.getItemIdsWithAction(action);
    }
    return updatedMetadata;
  }

  setItemsFromMetadata(metadata: Metadata) {
    const authzMap = new Map<string, AuthorizationViewModel>();
    for (const action of this.actions) {
      const tagKey = this.getTagKey(action);
      if (metadata.tags[tagKey]) {
        for (const tag of metadata.tags[tagKey]) {
          let authz = authzMap.get(tag);
          if (!authz) {
            authz = this.getItemFromTag(tag);
            authzMap.set(tag, authz);
          }
          authz.grantAction(action);
        }
      }
    }
    this.setItems([...authzMap.values()]);
    this.actionToggleMap.fromItems(this._items, this.actions);
  }

  getDeniedName(id: number) {
    let entity: string = '';
    switch (this.type.toLowerCase()) {
      case 'people':
        entity = 'this user ';
        break;
      case 'groups':
        entity = 'this group ';
        break;
      case 'roles':
        entity = 'this role ';
        break;
    }
    return `You are not authorized to view ${entity}(ID: ${id})`;
  }

  protected getItemFromTag(tag: string): AuthorizationViewModel {
    const authz = new AuthorizationViewModel();
    authz.id = parseInt(tag);
    return authz;
  }

  protected getItemIndex(item: AuthorizationViewModel) {
    return this._items.indexOf(item);
  }

  protected getTagKey(action: string) {
    return `${this.type.toLowerCase()}-${action}`
  }

  protected mergeAuthorizationViewModels(source: AuthorizationViewModel[]): AuthorizationViewModel[] {
    const authzMap = toDictionary(this._items, x => [x.id, x]);
    return source.map(x => {
      const authz = authzMap.get(x.id) || x;
      authz.grantAction("read");
      return authz;
    });
  }

  private getItemIdsWithAction(action: string) {
    return this._items
      .filter(x => x.hasAction(action) && x.id)
      .map(x => x.id.toString());
  }
}

export abstract class TypedAuthZCollection<TService, TModel, TResponse extends IResponse<TModel>, TDialogResult, TDialogData>
  extends GenericAuthzCollection {

  public abstract addButton: { icon: string, localizationKey: string, show: boolean };
  public abstract loadData(): void;

  protected abstract getItemsFromSource(): Observable<TResponse>
  protected abstract mapper(obj: TDialogResult): AuthorizationViewModel[];
  protected abstract buildDialogData(): TDialogData;


  constructor(
    tenantId: number,
    actions: string[],
    metadata: Metadata,
    type: string,
    icon: string,
    protected service: TService,
    private dialog: MatDialog,
    public dialogComponent: ComponentType<unknown>,
  ) {
    super(tenantId, actions, type, icon);
    this.setItemsFromMetadata(metadata);
  }

  addItems(metadata: Metadata): Observable<Metadata> {
    const config = {
      maxWidth: '90vh',
      width: '800px',
      data: this.buildDialogData()
    };
    const dialogRef = this.dialog.open(this.dialogComponent, config);
    return dialogRef.afterClosed().pipe(
      map(r => this.processDialogResult(r, metadata))
    );
  }

  protected mapNamesFromSource(nameMapper: (x: TModel) => [number, string]): void {
    if (this.hasValues) {
      this.log.debug('loading data for tenant', this.tenantId);
      const deniedMap = toDictionary(this.ids, x => [x, this.getDeniedName(x)]);
      this.getItemsFromSource().pipe(
        map(r => {
          if (r.items.length) {
            return toDictionary(r.items, nameMapper);
          } else {
            return deniedMap;
          }
        }),
        catchError(_ => of(deniedMap)),
        tap(nameMap => {
          for (const item of this._items) {
            item.name = nameMap.get(item.id);
          }
        })
      ).subscribe();
    }
  }

  protected processDialogResult(r: TDialogResult, metadata: Metadata) {
    if (r) {
      const selected = this.mapper(r).filter(x => x.id > 0);
      const data = this.mergeAuthorizationViewModels(selected);
      this.setItems(data);

      const tags = Object.keys(metadata.tags).filter(x => x.startsWith(AuthzConstants.EVERYONE_PREFIX));
      // check for everyone tags and auto toggle
      if (tags?.length > 0) {
        for (const tag of tags) {
          const tagAction = tag.substring(tag.indexOf('-') + 1);
          if (metadata?.tags[tag]?.indexOf('allow') > -1) {
            this.toggleActionForAllItems(tagAction, true);
          }
        }
      }
      return this.getUpdatedMetadata(metadata);
    }
    return metadata;
  }
}

export abstract class AuthZCollection<TService, TModel extends IHasName, TResponse>
  extends TypedAuthZCollection<TService, TModel, TResponse, AuthZDialogResult, AuthZDialogData> {

  constructor(
    protected auth: AuthService,
    actions: string[],
    metadata: Metadata,
    type: string,
    icon: string,
    service: TService,
    dialog: MatDialog,
    dialogComponent: ComponentType<unknown>,
  ) {
    super(
      parseInt(metadata.tenantId) || auth.user.snapshot.tenantId,
      actions, metadata, type, icon,
      service, dialog, dialogComponent
    )
  }

  public override loadData(): void {
    this.mapNamesFromSource(x => [x.id, x.name]);
  }

  protected override mapper(obj: AuthZDialogResult) {
    return obj.selected;
  }

  protected override buildDialogData(): AuthZDialogData {
    return <AuthZDialogData>{
      currentSelection: this._items,
      tenantId: this.tenantId
    }
  }
}

export abstract class AdminAuthZCollection
  extends TypedAuthZCollection<AdminsApi, AdminSummary, PagedAdminSummaryResponse, SelectUsersData, SelectUsersData> {

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private get adminsArgs(): any[] {
    return [
      1, // page
      this.ids.length, // pageSize
      'lastName,firstName', // sort
      this.ids, // ids
      //name, username, email, group, role, 
      null, null, null, null, null,
      this.tenantId, // tenantId
      //tenantName, state, credentialIds, externalIds
      null, null, null, null,
    ];
  }

  public override loadData(): void {
    this.mapNamesFromSource(x => [x.id, `${x.firstName} ${x.lastName}`]);
  }

  protected override mapper(obj: SelectUsersData): AuthorizationViewModel[] {
    return obj.selectedContacts.filter(u => u.adminId > 0)
      .map(u => {
        const authz = new AuthorizationViewModel();
        authz.id = u.adminId;
        authz.name = u.displayName;
        return authz;
      });
  }

  protected buildSelectUsersData(title: string, label: string, isSingleSelect: boolean): SelectUsersData {
    const data: SelectUsersData = {
      selectedContacts: this.map(u => new ContactSelection(true).withAdmin({ id: u.id })),
      permissions: [],
      includePermissions: true,
      saveButtonLabel: label,
      title,
      useV4Apis: true,
      tenantId: this.tenantId,
      isSingleSelect
    };
    return data;
  }

  protected override getItemsFromSource(): Observable<PagedAdminSummaryResponse> {
    return this.service.getAdminUsers(...this.adminsArgs);
  }
}

export class AuthorizationViewModel {
  id?: number;
  path?: string;
  actions?: string[] = [];
  selected: boolean = false;
  readonly: boolean = false;
  tags: string[] = [];

  constructor(public name?: string) { }

  hasAction(action: string): boolean {
    const index = this.actions.indexOf(action, 0);
    return index > -1;
  }

  grantAction(action: string): void {
    const index = this.actions.indexOf(action, 0);
    if (index === -1) {
      this.actions.push(action);
    }
  }

  revokeAction(action: string): void {
    const index = this.actions.indexOf(action, 0);
    if (index > -1) {
      this.actions.splice(index, 1);
    }
  }

  toggleAction(action: string): void {
    const index = this.actions.indexOf(action, 0);

    if (index > -1) {
      this.actions.splice(index, 1);
    } else {
      this.actions.push(action);
    }
  }

  isActionReadOnly(action: string): boolean {
    return this.readonly || action === 'read';
  }
}